import emailSpellChecker from '@zootools/email-spell-checker'
import { ref } from 'vue'
import { toast } from 'vue-sonner'

import { trans } from '/@front:shared/plugins/i18n'
import type { Account } from '/@front:shared/registration'
import { defaultAccountObject } from '/@front:shared/registration'
import { scrollErrorIntoView } from '/@front:shared/utils'

// `validate` and `forgetError` use precognition's literal-keyed field unions; the composable passes
// dynamic strings (e.g. `accounts.${index}.email`), so we type these as accepting `any` field at the
// FormLike boundary to stay assignable from precognition's `useForm` result.
type FormLike = {
  accounts: Account[]
  numberOfTenants: number
  errors: Record<string, unknown>
  validating: boolean
  processing: boolean
  validate: (field: any) => unknown
  forgetError: (field: any) => unknown
  submit: () => Promise<unknown>
}

export type SubmitOptions = {
  onSuccess?: (data: Record<string, unknown>) => boolean | void
  skipEmailValidation?: boolean
}

export function useRegistrationForm<T extends FormLike>(form: T, options: SubmitOptions = {}) {
  const didYouMean = ref<Map<number, string>>(new Map())

  const submitForm = () => {
    form.accounts = form.accounts.slice(0, form.numberOfTenants)

    Object.keys(form.errors).forEach((name) => {
      form.forgetError(name)
    })

    form
      .submit()
      .then((response) => {
        const data = (response as { data: Record<string, unknown> }).data

        if (options.onSuccess?.(data) === true) {
          return
        }

        if (typeof data.url === 'string' && data.url.length > 0) {
          location.assign(data.url)
          return
        }

        toast.error(trans('Something went wrong. Please try again'))
      })
      .catch((error: unknown) => {
        const status = (error as { response?: { status?: number } }).response?.status || 422
        if (status !== 422) {
          toast.error(trans('Something went wrong. Please try again'))
        }

        scrollErrorIntoView()
      })
  }

  const validateEmail = (index: number) => {
    if (options.skipEmailValidation) {
      return
    }

    if (form.validating || form.processing) {
      return
    }

    form.validate(`accounts.${index}.email`)

    const suggestedEmail = emailSpellChecker.run({
      email: form.accounts[index].email,
    })

    if (suggestedEmail?.full) {
      didYouMean.value.set(index, suggestedEmail.full)
    }
  }

  const applyDidYouMean = (index: number) => {
    const email = didYouMean.value.get(index)
    if (!email) {
      return
    }

    form.accounts[index].email = email
    form.validate(`accounts.${index}.email`)

    didYouMean.value.delete(index)
  }

  const padAccountsTo4 = () => {
    while (form.accounts.length < 4) {
      form.accounts.push(structuredClone(defaultAccountObject))
    }
  }

  const fillCurrentProfile = (profile: Partial<T>, maxTenants = 4) => {
    for (const key of Object.keys(profile) as (keyof T)[]) {
      if (key === 'accounts' || key === 'numberOfTenants') {
        continue
      }
      form[key] = profile[key] as T[keyof T]
    }

    if (typeof profile.numberOfTenants === 'number') {
      form.numberOfTenants = Math.min(maxTenants, profile.numberOfTenants)
    }

    profile.accounts?.forEach((account, index) => {
      form.accounts.splice(index, 1, {
        ...structuredClone(defaultAccountObject),
        ...structuredClone(account),
      })
    })
  }

  return {
    didYouMean,
    submitForm,
    validateEmail,
    applyDidYouMean,
    padAccountsTo4,
    fillCurrentProfile,
  }
}
